import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { checkAcceptedFileTypes, checkDocumentFileTypes, validateFileSize } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { CustomFormLabel, ErrorMessage, FileErrorMessage, FileInputButton } from 'components';
import { Box, Typography, IconButton } from '@mui/material';
import * as icons from 'mdi-material-ui';

const MultipleDocumentUpload = ({
    selectedFiles = [],
    removeFile,
    ContainerProps,
    handleFileChange,
    label,
    inline,
    filesNamesInline,
    id,
    error,
    width = 'auto',
    isTableCell,
    acceptedFileTypes,
    type = 'default', // 'default' uses checkAcceptedFileTypes, 'document' uses checkDocumentFileTypes
}) => {
    const [fileSizeError, setFileSizeError] = useState(false);
    const [fileTypeError, setFileTypeError] = useState(false);

    const handleInterceptChange = useCallback(
        e => {
            const uploadedFiles = Array.from(e.target.files);

            // Validate all files before adding any
            const invalidFiles = [];
            const validFiles = [];

            uploadedFiles.forEach(file => {
                if (!validateFileSize(file)) {
                    invalidFiles.push({ file, error: 'size' });
                    return;
                }

                // Check file type using the appropriate helper function based on type prop
                const fileTypeChecker =
                    type === 'document' ? checkDocumentFileTypes : checkAcceptedFileTypes;
                fileTypeChecker(
                    file,
                    () => {
                        validFiles.push(file);
                    },
                    () => {
                        invalidFiles.push({ file, error: 'type' });
                    }
                );
            });

            // Show error for first invalid file
            if (invalidFiles.length > 0) {
                const firstError = invalidFiles[0];
                if (firstError.error === 'size') {
                    setFileSizeError(true);
                    setFileTypeError(false);
                } else {
                    setFileTypeError(true);
                    setFileSizeError(false);
                }
            }

            // All files are valid
            if (validFiles.length > 0) {
                setFileSizeError(false);
                setFileTypeError(false);
                handleFileChange(validFiles);
            }

            // Reset the input value to allow selecting the same files again
            e.target.value = '';
        },
        [handleFileChange, type]
    );

    const files = useMemo(() => {
        if (!selectedFiles) return [];
        return Array.isArray(selectedFiles) ? selectedFiles.filter(f => !!f) : [];
    }, [selectedFiles]);

    return (
        <Box mt="20px" mb={5} width={width} {...ContainerProps}>
            {label && <CustomFormLabel>{label}</CustomFormLabel>}
            <Box
                mb={1}
                display={filesNamesInline ? 'block' : 'flex'}
                flexDirection="column"
                width="100%"
            >
                {files.map((file, i) => (
                    <Box
                        key={file.name + i}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        flex="0 1 100%"
                        mb={1}
                    >
                        <Typography
                            key={file.name}
                            sx={{
                                textAlign: 'left',
                                maxWidth: isTableCell ? '250px' : '90%',
                            }}
                            title={`${texts.UPLOADED_FILE_NAME} : ${file.name || texts.AUCUN}`}
                            variant="body2"
                            noWrap
                        >{`${texts.UPLOADED_FILE_NAME} : ${file.name || texts.AUCUN}`}</Typography>
                        {!!file.name && (
                            <IconButton size="small" onClick={() => removeFile(i)}>
                                <icons.Delete />
                            </IconButton>
                        )}
                    </Box>
                ))}
                {files.length === 0 && (
                    <Typography variant="body2" color="textSecondary" mb={1}>
                        Aucun document ajouté
                    </Typography>
                )}
                {inline && (
                    <FileInputButton
                        sx={
                            isTableCell
                                ? {
                                      minWidth: '130px',
                                      flex: '1 1 130px',
                                      margin: 'auto',
                                  }
                                : undefined
                        }
                        noMargin
                        acceptedFileTypes={acceptedFileTypes}
                        showIcon
                        label={texts.UPLOAD_FILE_BUTTON}
                        onFileChange={handleInterceptChange}
                        id={id}
                        multiple
                    />
                )}
            </Box>
            {fileSizeError && <FileErrorMessage variant="size" useMargin={false} />}
            {fileTypeError && <FileErrorMessage variant="type" useMargin={false} />}
            <ErrorMessage gutterBottom message={error} />
            {!inline && (
                <FileInputButton
                    sx={
                        isTableCell
                            ? {
                                  minWidth: '130px',
                                  flex: '1 1 130px',
                                  margin: 'auto',
                              }
                            : undefined
                    }
                    acceptedFileTypes={acceptedFileTypes}
                    noMargin
                    showIcon
                    label={texts.UPLOAD_FILE_BUTTON}
                    onFileChange={handleInterceptChange}
                    id={id}
                    multiple
                />
            )}
        </Box>
    );
};

MultipleDocumentUpload.propTypes = {
    id: PropTypes.string,
    inline: PropTypes.bool,
    isTableCell: PropTypes.bool,
    selectedFiles: PropTypes.array,
    removeFile: PropTypes.func,
    ContainerProps: PropTypes.object,
    handleFileChange: PropTypes.func,
    label: PropTypes.string,
    filesNamesInline: PropTypes.bool,
    error: PropTypes.string,
    width: PropTypes.string,
    acceptedFileTypes: PropTypes.string,
    type: PropTypes.oneOf(['default', 'document']),
};

export default MultipleDocumentUpload;
